// eslint-disable-next-line no-restricted-imports
import { TextInput, type TextInputProps, View } from "react-native"
import { cn } from "@/components/util/cn"
import React, { forwardRef, type ReactNode } from "react"
import { type NativeSyntheticEvent } from "react-native/Libraries/Types/CoreEventTypes"
import { type TextInputFocusEventData } from "react-native/Libraries/Components/TextInput/TextInput"

export const BFTextInputVariants = {
  standard: {
    containerClassName: "",
    className:
      "web:focus:outline-none color-darkest text-[16px] font-spacegrotesk",
  },
  form: {
    containerClassName: "w-full rounded bg-white border border-light-grey",
    className:
      "web:focus:outline-none w-full px-3 py-4 font-spacegrotesk text-midnight-blue",
  },
  formFocused: {
    containerClassName: "w-full rounded bg-white border border-calm-blue",
    className:
      "web:focus:outline-none w-full px-3 py-4 font-spacegrotesk color-darkest text-[16px]",
  },
  formError: {
    containerClassName: "w-full rounded bg-white border border-red-orange",
    className:
      "web:focus:outline-none w-full px-3 py-4 font-spacegrotesk text-midnight-blue",
  },
  formDark: {
    containerClassName: "w-full rounded bg-white border border-warm-grey",
    className: "w-full px-3 py-4 font-spacegrotesk text-midnight-blue",
  },
  formErrorDark: {
    containerClassName: "w-full rounded bg-white border border-salmon",
    className:
      "web:focus:outline-none w-full px-3 py-4 font-spacegrotesk text-midnight-blue",
  },
}

type BFVariant = keyof typeof BFTextInputVariants

export interface BFBaseInputProps {
  variant?: BFVariant
  className?: string
}

export interface BFTextInputContainerProps extends BFBaseInputProps {
  fsClass?: string
  accessoryLeft?: ReactNode
  accessoryRight?: ReactNode
}

export interface BFTextInputProps
  extends TextInputProps,
    BFTextInputContainerProps {
  fsClass?: string
  containerClassName?: string
  inputClassName?: string
}

interface BFSimpleTextInputProps extends TextInputProps, BFBaseInputProps {
  fsClass?: string
  containerClassName?: string
  inputClassName?: string
}

export const BFTextInputContainer = ({
  variant,
  className,
  accessoryLeft,
  children,
  accessoryRight,
  fsClass: _fsClass, // eslint-disable-line @typescript-eslint/no-unused-vars
}: BFTextInputContainerProps & { children?: ReactNode }) => {
  return (
    <View
      className={cn(
        variant && BFTextInputVariants[variant]?.containerClassName,
        "flex-row items-center gap-0",
        className
      )}
    >
      {accessoryLeft && <View className={"pl-3"}>{accessoryLeft}</View>}
      <View className={"flex-1"}>{children}</View>
      {accessoryRight && <View className={"pr-3"}>{accessoryRight}</View>}
    </View>
  )
}

export const BFSimpleTextInput = forwardRef<TextInput, BFSimpleTextInputProps>(
  (
    {
      variant,
      className,
      allowFontScaling = false,
      fsClass = "fs-unmask",
      ...props
    },
    ref
  ) => {
    return (
      <TextInput
        ref={ref}
        fsClass={fsClass}
        allowFontScaling={allowFontScaling}
        className={cn(
          variant && BFTextInputVariants[variant]?.containerClassName,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          variant && BFTextInputVariants[variant]?.className,
          className
        )}
        {...props}
      />
    )
  }
)

export const BFTextInput = forwardRef<TextInput, BFTextInputProps>(
  (
    {
      variant,
      className,
      containerClassName,
      inputClassName,
      allowFontScaling = false,
      fsClass = "fs-unmask",
      accessoryRight,
      accessoryLeft,
      onBlur,
      onFocus,
      ...props
    },
    ref
  ) => {
    const [focus, setFocus] = React.useState(false)

    function doOnFocus(e: NativeSyntheticEvent<TextInputFocusEventData>) {
      setFocus(true)
      if (onFocus) {
        onFocus(e)
      }
    }

    function doOnBlur(e: NativeSyntheticEvent<TextInputFocusEventData>) {
      setFocus(false)
      if (onBlur) {
        onBlur(e)
      }
    }

    return (
      <BFTextInputContainer
        className={cn(
          "bg-off-white",
          focus && "border-calm-blue",
          containerClassName || className
        )}
        variant={variant}
        fsClass={fsClass}
        accessoryLeft={accessoryLeft}
        accessoryRight={accessoryRight}
      >
        <TextInput
          hitSlop={16}
          ref={ref}
          onBlur={doOnBlur}
          onFocus={doOnFocus}
          fsClass={fsClass}
          allowFontScaling={allowFontScaling}
          className={cn(
            "web:focus:outline-none placeholder:text-warm-grey placeholder:font-spacegrotesk",
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            variant && BFTextInputVariants[variant]?.className,
            inputClassName || className,
            "border-0 bg-transparent outline-none"
          )}
          {...props}
        />
      </BFTextInputContainer>
    )
  }
)
